import { useState, useEffect, useCallback, Fragment } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import api, { reportsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import CustomerDrillDownDialog from '@/components/CustomerDrillDownDialog';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import { toast } from 'sonner';
import { DownloadSimple, ArrowsClockwise, X } from '@phosphor-icons/react';

const money = (v) => (v === '' || v == null) ? '' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtD = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '';

export default function SalesOrderReportPage() {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? `${now.getFullYear()}-04-01` : `${now.getFullYear() - 1}-04-01`;
  const fyEnd = now.getMonth() >= 3 ? `${now.getFullYear() + 1}-03-31` : `${now.getFullYear()}-03-31`;

  const [from, setFrom] = useState(fyStart);
  const [to, setTo] = useState(fyEnd);
  const [location, setLocation] = useState('');
  const [status, setStatus] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [salesperson, setSalesperson] = useState('');
  const [customer, setCustomer] = useState('');
  const [includeST, setIncludeST] = useState(false);
  const [groupBy, setGroupBy] = useState('location');

  const [options, setOptions] = useState({ locations: [], statuses: [], lead_sources: [], salespersons: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    reportsAPI.salesOrderReportFilterOptions().then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = { start_date: from, end_date: to, include_stock_transfer: includeST, group_by: groupBy };
    if (location) p.location = location;
    if (status) p.status = status;
    if (leadSource) p.lead_source = leadSource;
    if (salesperson) p.salesperson = salesperson;
    if (customer) p.customer = customer;
    return p;
  }, [from, to, includeST, groupBy, location, status, leadSource, salesperson, customer]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.salesOrderReport(buildParams());
      setData(data);
    } catch { toast.error('Failed to load sales order report'); }
    finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setLocation(''); setStatus(''); setLeadSource(''); setSalesperson(''); setCustomer('');
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const resp = await api.get('/reports/sales-order-report/export', { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `sales_order_details_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const groups = data?.groups || [];
  const grouped = groupBy === 'location';

  return (
    <div className="p-6" data-testid="sales-order-report-page">
      <PageHeader title="Sales Order Report" subtitle="Sales Order Details — grouped by location with subtotals, filters & export" />

      {/* Filters */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">FROM</label>
            <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="sor-from" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">TO</label>
            <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 text-xs w-36" data-testid="sor-to" />
          </div>
          <Button size="sm" variant="outline" onClick={() => { setFrom(fyStart); setTo(fyEnd); }} className="h-8 text-xs">Current FY</Button>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">GROUP BY</label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="h-8 text-xs w-32" data-testid="sor-group-by"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="location">Location</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-xs text-[#0F172A] ml-2" data-testid="sor-st-toggle">
            <input type="checkbox" checked={includeST} onChange={e => setIncludeST(e.target.checked)} className="accent-[#0F4C81]" />
            Include Stock Transfers
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <FilterSelect label="LOCATION" value={location} setValue={setLocation} options={options.locations} placeholder="All Locations" testId="sor-location" w="w-44" />
          <FilterSelect label="STATUS" value={status} setValue={setStatus} options={options.statuses} placeholder="All Status" testId="sor-status" w="w-40" />
          <FilterSelect label="LEAD SOURCE" value={leadSource} setValue={setLeadSource} options={options.lead_sources} placeholder="All Sources" testId="sor-lead-source" w="w-40" />
          <FilterSelect label="SALES PERSON" value={salesperson} setValue={setSalesperson} options={options.salespersons} placeholder="All Sales Persons" testId="sor-salesperson" w="w-44" />
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">CUSTOMER</label>
            <Input value={customer} onChange={e => setCustomer(e.target.value)} placeholder="Name / phone" className="h-8 text-xs w-40" data-testid="sor-customer" />
          </div>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={clearFilters} className="h-8 text-xs"><X size={12} className="mr-1" /> Clear</Button>
            <Button size="sm" onClick={load} className="h-8 text-xs bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="sor-apply"><ArrowsClockwise size={12} className="mr-1" /> Apply</Button>
            <Button size="sm" variant="outline" onClick={exportExcel} disabled={exporting} className="h-8 text-xs" data-testid="sor-export"><DownloadSimple size={12} className="mr-1" /> {exporting ? 'Exporting…' : 'Export Excel'}</Button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        <div className="rounded-xl p-4 bg-[#0F4C81] text-white" data-testid="sor-sum-total">
          <p className="text-[10px] uppercase tracking-wide opacity-70">Grand Total</p>
          <p className="text-xl font-bold mt-1">{money(data?.grand_total)}</p>
        </div>
        <div className="rounded-xl p-4 bg-blue-50 text-blue-800" data-testid="sor-sum-orders">
          <p className="text-[10px] uppercase tracking-wide opacity-70">Total Orders</p>
          <p className="text-xl font-bold mt-1">{(data?.total_orders || 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="rounded-xl p-4 bg-[#EBF3FA] text-[#0F4C81]" data-testid="sor-sum-groups">
          <p className="text-[10px] uppercase tracking-wide opacity-70">{grouped ? 'Locations' : 'Groups'}</p>
          <p className="text-xl font-bold mt-1">{groups.length}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto mt-6">
        {loading ? (
          <div className="py-16 text-center"><div className="inline-block w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <table className="w-full text-xs" data-testid="sor-table">
            <thead>
              <tr className="bg-[#0F4C81] text-white text-[10px] uppercase">
                <th className="text-left p-2.5 min-w-[180px]">Customer Name</th>
                <th className="text-left p-2.5">Sales Order#</th>
                <th className="text-left p-2.5">Date</th>
                <th className="text-right p-2.5">Amount</th>
                <th className="text-left p-2.5">Sales Person</th>
                <th className="text-left p-2.5">Lead Source</th>
                <th className="text-left p-2.5">Location</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g, gi) => (
                <Fragment key={`grp-${gi}`}>
                  {grouped && (
                    <tr key={`g-${gi}`} className="bg-[#EBF3FA] border-t border-[#E2E8F0]" data-testid={`sor-group-${gi}`}>
                      <td className="p-2.5 font-semibold text-[#0F4C81]" colSpan={3}>{g.location} <span className="text-[#64748B] font-normal">({g.count})</span></td>
                      <td className="p-2.5 text-right font-semibold text-[#0F4C81]">{money(g.subtotal)}</td>
                      <td colSpan={3} />
                    </tr>
                  )}
                  {g.orders.map((r, i) => (
                    <tr key={`${gi}-${i}`} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`sor-row-${gi}-${i}`}>
                      <td className="p-2.5">
                        <button onClick={() => setDrill({ id: r.customer_id, name: r.customer_name })} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left">{r.customer_name}</button>
                      </td>
                      <td className="p-2.5 font-mono text-[#0F4C81]">{r.so_number}</td>
                      <td className="p-2.5">{fmtD(r.date)}</td>
                      <td className="p-2.5 text-right font-medium">{money(r.amount)}</td>
                      <td className="p-2.5">{r.salesperson}</td>
                      <td className="p-2.5">{r.lead_source}</td>
                      <td className="p-2.5">{r.location}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {!loading && !groups.length && (
                <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">No sales orders match the selected filters.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <CustomerDrillDownDialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }} customerId={drill?.id || ''} customerName={drill?.name || ''} />
    </div>
  );
}

const FilterSelect = ({ label, value, setValue, options, placeholder, testId, w }) => (
  <div>
    <label className="text-[10px] text-[#64748B] block mb-1">{label}</label>
    <MultiSelectFilter
      value={String(value || '').split(',').filter(Boolean)}
      onChange={(vals) => setValue(vals.join(','))}
      options={(options || []).map(o => ({ value: o, label: o }))}
      allLabel={placeholder} noun="selected" testid={testId}
      buttonClassName={`h-8 text-xs ${w}`}
    />
  </div>
);
